<script setup lang="ts">
/**
 * "คำสอน AI" — a "/ln …" typed on a TEST account (api/app/ai/learn.py). Not a customer bubble: the AI never
 * reads it and it never waits for a reply. Shows what was taught, the AI/staff message it answers, the proposed rule
 * (กติกาการตอบ) and its state; the owner approves / edits / declines right here (same rules as /knowledge).
 * When the AI found it contradicts a rule still in use, approve becomes a choice: use the new one instead (the old one
 * stops being used), use both (the AI was wrong), or not the new one.
 */
import { computed, nextTick, ref } from 'vue'
import { getApi } from '../../api'
import type { LearnItem } from '../../api/types'
import { clockTime } from '../../lib/inbox'
import {
  answeringWho,
  choiceLabels,
  LEARN_RULE_MAX,
  learnPerms,
  learnStatus,
  liveConflicts,
  OVERLAP_STATUS,
  overlapTitle,
  rewriteNote,
} from '../../lib/learn'
import { useInbox } from '../../stores/inbox'
import { useSession } from '../../stores/session'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ item: LearnItem }>()
const inbox = useInbox()
const session = useSession()

const it = computed(() => props.item)
const status = computed(() => learnStatus(it.value))
const perms = computed(() => learnPerms(session.me?.role, it.value))
const note = computed(() => rewriteNote(it.value))
const who = computed(() => answeringWho(it.value))
const waiting = computed(() => it.value.rule?.status === 'pending_approval')
// conflicts first, then repeats — each with its status now (a replaced rule shows "ปิดใช้แล้ว")
const overlaps = computed(() => [
  ...it.value.overlaps.filter((o) => o.relation === 'conflict'),
  ...it.value.overlaps.filter((o) => o.relation === 'same'),
])
const ovTitle = computed(() => overlapTitle(it.value, session.me?.role === 'owner'))
const liveConf = computed(() => liveConflicts(it.value.overlaps))
const labels = computed(() => choiceLabels(liveConf.value.length))

const editing = ref(false)
const draft = ref('')
const busy = ref(false)
const error = ref<string | null>(null)
const box = ref<HTMLTextAreaElement | null>(null)
const len = computed(() => draft.value.trim().length)
const tooLong = computed(() => len.value > LEARN_RULE_MAX)

function autosize(): void {
  const el = box.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}

function startEdit(): void {
  draft.value = it.value.rule?.text ?? ''
  error.value = null
  editing.value = true
  void nextTick(() => {
    autosize()
    box.value?.focus()
  })
}

async function run(fn: () => Promise<{ item: LearnItem }>): Promise<boolean> {
  busy.value = true
  error.value = null
  try {
    const res = await fn()
    inbox.replaceItem(it.value.conversation_id, res.item)
    return true
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e)
    return false
  } finally {
    busy.value = false
  }
}

async function approve(asEdited = false): Promise<void> {
  const text = asEdited ? draft.value.trim() : undefined
  if (await run(() => getApi().learnAction(it.value.id, 'approve', text && text !== it.value.rule?.text ? text : undefined)))
    editing.value = false
}
async function replace(): Promise<void> {
  await run(() => getApi().learnAction(it.value.id, 'replace'))
}
async function reject(): Promise<void> {
  await run(() => getApi().learnAction(it.value.id, 'reject'))
}
async function save(): Promise<void> {
  const text = draft.value.trim()
  if (text === it.value.rule?.text) {
    editing.value = false
    return
  }
  if (await run(() => getApi().learnEdit(it.value.id, text))) editing.value = false
}
function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') editing.value = false
  if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
    e.preventDefault()
    if (len.value >= 2 && !tooLong.value) void save()
  }
}
const saveLabel = computed(() =>
  session.me?.role === 'owner' ? 'บันทึก' : it.value.rule?.status === 'approved' ? 'ส่งให้เจ้าของอนุมัติ' : 'บันทึกข้อเสนอ',
)
</script>

<template>
  <div class="lc" role="note" :aria-label="`คำสอน AI: ${it.text}`">
    <div class="lc-card" :class="`t-${status.tone}`">
      <div class="lc-h">
        <Icon name="sparkles" />
        <b>คำสอน AI</b>
        <span class="lc-sub" title="พิมพ์ /ln จากบัญชีทดสอบ ลูกค้าไม่เห็น และ AI ไม่ถือเป็นข้อความลูกค้า">เห็นเฉพาะทีม</span>
        <span class="grow"></span>
        <span class="lc-chip" :class="`t-${status.tone}`">{{ status.label }}</span>
        <span class="mono lc-t">{{ clockTime(it.at) }}</span>
      </div>

      <div v-if="it.answering" class="lc-ans">
        <span class="lc-k">ตอบต่อจากข้อความของ {{ who }}</span>
        <p class="lc-q">{{ it.answering.text }}</p>
      </div>

      <div class="lc-taught">
        <span class="lc-k">สิ่งที่สอน</span>
        <p class="lc-x">{{ it.text || '(ไม่มีข้อความ)' }}</p>
      </div>

      <div v-if="it.rule" class="lc-rule" :class="{ off: status.tone === 'off' }">
        <span class="lc-k">กติกาที่ AI จะใช้<template v-if="it.rule.edited && it.rule.edited_by"> · แก้โดย {{ it.rule.edited_by }}</template></span>
        <p v-if="!editing" class="lc-r">{{ it.rule.text }}</p>
        <div v-else class="lc-edit">
          <textarea
            ref="box"
            v-model="draft"
            rows="1"
            aria-label="แก้กติกา"
            :maxlength="LEARN_RULE_MAX + 40"
            @input="autosize"
            @keydown="onKey"
          ></textarea>
          <div class="lc-edit-f">
            <span class="lc-count mono" :class="{ over: tooLong }">{{ len }}/{{ LEARN_RULE_MAX }}</span>
            <span class="grow"></span>
            <button class="lc-tl" :disabled="busy" @click="editing = false">ยกเลิก</button>
            <button class="lc-btn" :disabled="busy || len < 2 || tooLong" @click="save">{{ saveLabel }}</button>
            <!-- with a live conflict the owner saves, then picks one of the three below -->
            <button v-if="perms.approve && !perms.choose" class="lc-btn pri" :disabled="busy || len < 2 || tooLong" @click="approve(true)">
              <Icon name="check" />อนุมัติตามที่แก้
            </button>
          </div>
        </div>
        <p v-if="note && !editing" class="lc-note" :class="{ live: it.rewrite === 'pending' }">{{ note }}</p>
        <p v-if="it.rule.pending_text" class="lc-note">
          ข้อเสนอแก้จาก {{ it.rule.pending_by ?? 'ผู้จัดการ' }}: {{ it.rule.pending_text }}
        </p>
        <p v-if="it.rule.status === 'approved' && it.rule.approved_by" class="lc-note ok">
          <Icon name="checkCircle" />อนุมัติโดย {{ it.rule.approved_by
          }}<template v-if="it.rule.approved_at"> · {{ clockTime(it.rule.approved_at) }}</template> · ใช้กับทั้งร่างคำตอบและ AI ดูแลแชท
        </p>
      </div>

      <p v-if="it.note" class="lc-warn" :class="{ crit: it.unsafe }"><Icon name="alertCircle" />{{ it.note }}</p>

      <div v-if="ovTitle && status.tone !== 'off'" class="lc-ov" :class="{ calm: !liveConf.length }">
        <span class="lc-k"><Icon :name="liveConf.length ? 'alert' : 'checkCircle'" />{{ ovTitle }}</span>
        <ul>
          <li v-for="o in overlaps" :key="o.rule_id" :class="{ gone: o.status === 'retired' || o.status === 'rejected' }">
            <span v-if="o.status" class="lc-ov-s">{{ OVERLAP_STATUS[o.status] }}</span>
            <span class="lc-ov-t">{{ o.text }}</span>
            <span v-if="o.why" class="lc-ov-w">{{ o.why }}</span>
          </li>
        </ul>
        <!-- the question and its answers together: approve becomes a choice of three -->
        <div v-if="perms.choose && !editing" class="lc-ov-act">
          <button class="lc-tl crit" :disabled="busy" @click="reject">ไม่ใช้ข้อใหม่</button>
          <button class="lc-btn" :disabled="busy" title="AI ตรวจพลาด ไม่ได้ขัดกันจริง ใช้ทุกข้อต่อไป" @click="approve()">
            {{ labels.both }}
          </button>
          <button class="lc-btn pri" :disabled="busy" title="อนุมัติข้อใหม่ และปิดใช้ข้อเดิมที่ขัดกัน (ย้อนกลับได้ในประวัติการแก้)" @click="replace">
            <Icon name="check" />{{ labels.replace }}
          </button>
        </div>
      </div>

      <div v-if="!editing" class="lc-f">
        <RouterLink class="lc-link" :to="{ name: 'knowledge', params: { section: 'rules' } }"
          ><Icon name="book" />ดูในคลังความรู้</RouterLink
        >
        <span class="grow"></span>
        <span v-if="waiting && !perms.approve" class="lc-wait">เจ้าของเป็นคนอนุมัติ</span>
        <template v-if="perms.choose">
          <button v-if="perms.edit" class="lc-tl" :disabled="busy" @click="startEdit"><Icon name="pencil" />แก้ไข</button>
        </template>
        <template v-else>
          <button v-if="perms.reject" class="lc-tl crit" :disabled="busy" @click="reject">ไม่ใช้</button>
          <button v-if="perms.edit" class="lc-tl" :disabled="busy" @click="startEdit"><Icon name="pencil" />แก้ไข</button>
          <button v-if="perms.approve" class="lc-btn pri" :disabled="busy" @click="approve()"><Icon name="check" />อนุมัติ</button>
        </template>
      </div>
      <p v-if="error" class="lc-err" role="alert">{{ error }}</p>
    </div>
  </div>
</template>

<style scoped>
.lc {
  display: flex;
  justify-content: center;
}
.lc-card {
  width: min(600px, 100%);
  padding: 10px 14px 12px 16px;
  border-radius: 12px;
  background: #f4faf7;
  border: 1px solid #cfe5d9;
  box-shadow: inset 3px 0 0 #2f9e75;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.lc-card.t-off {
  background: #f7f8f8;
  border-color: var(--line);
  box-shadow: inset 3px 0 0 #b8c4be;
}
.lc-h {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  min-height: 24px;
  font-size: 12px;
  line-height: 18px;
  color: #2d5a4b;
  white-space: nowrap;
}
.lc-h > :deep(.ic) {
  width: 15px;
  height: 15px;
  color: #1f8a63;
}
.lc-h b {
  font-weight: 600;
  font-size: 12.5px;
  line-height: 19px;
  color: #17211c;
}
.lc-sub {
  color: #56655e;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lc-sub::before {
  content: '·';
  margin-right: 6px;
  color: #9fb3a9;
}
.lc-chip {
  flex: none;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}
.lc-chip.t-wait {
  background: var(--warns);
  color: var(--warn);
}
.lc-chip.t-ok {
  background: #dff1e8;
  color: #0a5e47;
}
.lc-chip.t-off,
.lc-chip.t-none {
  background: #eceff0;
  color: #56655e;
}
.lc-t {
  flex: none;
  color: var(--mut);
  font-size: 12px;
}
.lc-k {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  line-height: 18px;
  color: #56655e;
  font-weight: 500;
}
.lc-k :deep(.ic) {
  width: 13px;
  height: 13px;
}
.lc-ans .lc-q {
  margin-top: 2px;
  padding: 6px 10px;
  border-radius: 8px;
  background: #fff;
  border: 1px dashed #b6d9c6;
  font-size: 13px;
  line-height: 20px;
  color: #3e4d46;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}
.lc-x {
  margin-top: 1px;
  font-size: 13.5px;
  line-height: 22px;
  color: #17211c;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.lc-rule {
  padding: 8px 10px 9px;
  border-radius: 9px;
  background: #fff;
  border: 1px solid #d7e8df;
}
.lc-r {
  margin-top: 2px;
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
  color: #17211c;
  overflow-wrap: anywhere;
}
.lc-rule.off .lc-r {
  color: #56655e;
  font-weight: 400;
  text-decoration: line-through;
  text-decoration-color: #b8c4be;
}
.lc-note {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin-top: 4px;
  font-size: 12px;
  line-height: 18px;
  color: #56655e;
}
.lc-note :deep(.ic) {
  flex: none;
  width: 13px;
  height: 13px;
  margin-top: 2px;
}
.lc-note.ok {
  color: #0a5e47;
}
.lc-note.live {
  color: #1f8a63;
}
.lc-warn {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  background: var(--warns);
  color: #6b4300;
  font-size: 12.5px;
  line-height: 19px;
}
.lc-warn.crit {
  background: var(--crits);
  color: var(--crit);
}
.lc-warn :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
  margin-top: 2px;
}
.lc-ov {
  padding: 7px 10px 8px;
  border-radius: 8px;
  background: #fff8e6;
  border: 1px solid #f0dca6;
}
.lc-ov .lc-k {
  color: #6b4300;
}
.lc-ov ul {
  margin: 4px 0 0;
  padding: 0 0 0 18px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.lc-ov li {
  font-size: 12.5px;
  line-height: 19px;
  color: #3d3320;
}
.lc-ov-t {
  overflow-wrap: anywhere;
}
.lc-ov.calm {
  background: #f7f8f8;
  border-color: var(--line);
}
.lc-ov.calm .lc-k {
  color: #4a5a52;
}
.lc-ov.calm .lc-ov-w {
  color: var(--mut);
}
.lc-ov-act {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0dca6;
}
.lc-ov li.gone .lc-ov-t {
  color: #7d8a84;
  text-decoration: line-through;
  text-decoration-color: #b8c4be;
}
.lc-ov-s {
  display: inline-block;
  margin-right: 6px;
  padding: 0 6px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.05);
  color: #56655e;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
  vertical-align: 1px;
}
.lc-ov-w {
  display: block;
  color: #8a6a2a;
  font-size: 12px;
  line-height: 18px;
}
.lc-f {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.lc-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  line-height: 20px;
  color: var(--ac2);
  font-weight: 500;
  text-decoration: none;
}
.lc-link:hover {
  text-decoration: underline;
  text-underline-offset: 2px;
}
.lc-link :deep(.ic) {
  width: 14px;
  height: 14px;
}
.lc-wait {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.lc-tl,
.lc-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}
.lc-tl {
  color: #3e4d46;
}
.lc-tl:hover:not(:disabled) {
  background: #e3efe8;
}
.lc-tl.crit {
  color: var(--crit);
}
.lc-tl.crit:hover:not(:disabled) {
  background: var(--crits);
}
.lc-btn {
  border: 1px solid #cfe0d6;
  background: #fff;
  color: #17211c;
}
.lc-btn.pri {
  background: var(--ac);
  border-color: var(--ac);
  color: #fff;
}
.lc-btn.pri:hover:not(:disabled) {
  background: var(--ac2);
}
.lc-tl :deep(.ic),
.lc-btn :deep(.ic) {
  width: 14px;
  height: 14px;
}
.lc-tl:disabled,
.lc-btn:disabled {
  opacity: 0.55;
  cursor: default;
}
.lc-edit textarea {
  display: block;
  width: 100%;
  margin-top: 4px;
  padding: 6px 10px;
  border: 1px solid #b6d9c6;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  line-height: 22px;
  resize: none;
  outline: none;
}
.lc-edit textarea:focus {
  border-color: var(--ac);
  box-shadow: 0 0 0 3px #d7eee3;
}
.lc-edit-f {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  flex-wrap: wrap;
}
.lc-count {
  font-size: 12px;
  color: var(--mut);
}
.lc-count.over {
  color: var(--crit);
}
.lc-err {
  font-size: 12.5px;
  line-height: 19px;
  color: var(--crit);
}
.grow {
  flex: 1;
}
</style>
